import { useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  Alert,
  Button,
  DescriptionList,
  LoadingState,
  ErrorState,
  Tabs,
  Text,
  Badge,
} from "@marekl/adminer/components";
import { RouteModal, useCloseRouteModal } from "@marekl/adminer/shell";
import { useConfirm } from "@marekl/adminer/hooks";
import { notifySuccess } from "@marekl/adminer/api";
import { formatDateTime } from "@marekl/adminer/format";
import { useIsAdmin } from "../../auth/useAuth";
import {
  isLiveState,
  useMessageAction,
  useMessageDetail,
  type MessageDetail,
} from "../../lib/queries/messages";
import { JsonBlock } from "../../components/JsonBlock";
import { smsHint } from "../../lib/sms";
import { MailBarcode } from "../../components/MailBarcode";
import { Postmark } from "../../components/Postmark";
import { SenderChip } from "../../components/SenderChip";
import { DeliveryTimeline } from "./DeliveryTimeline";
import { MessageStateBadge } from "./MessageStateBadge";
import { RedeliverModal } from "./RedeliverModal";
import {
  ACTION_COPY,
  CANCELLABLE_STATES,
  type ConfirmedAction,
  REDELIVERABLE_STATES,
  RETRYABLE_STATES,
  labelCategory,
} from "./messageConstants";
import styles from "./MessagesPage.module.css";

/**
 * One strip for everything below the envelope. The timeline leads: what
 * happened to the message is the question this dialog is opened to answer,
 * and the rendered body is a reference you go to second.
 */
type DetailTab = "timeline" | "html" | "text";

/**
 * Message detail at /messages/:id — a route modal nested under the messages
 * list route, so the (still-filtered) list stays mounted underneath and the
 * URL is deep-linkable / ⌘-clickable. Row links carry the list's search
 * params into this URL, and closing keeps them: on a data router
 * `useCloseRouteModal` always navigates to the fallback (there is no
 * background-location state), so the fallback re-attaches the current search.
 *
 * A side-drawer presentation was evaluated and rejected: the lib's shell
 * drawer is the mobile *navigation* drawer, and the component kit ships no
 * generic drawer/side-panel primitive to build on.
 */
export function MessageDetailRouteModal() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const close = useCloseRouteModal({
    pathname: "/messages",
    search: location.search,
  });
  const isAdmin = useIsAdmin();
  const confirm = useConfirm();
  const messageAction = useMessageAction();
  const detail = useMessageDetail(id ?? null);
  const [tab, setTab] = useState<DetailTab>("timeline");
  // Redeliver asks for the recipient, so it has its own form rather than
  // the shared confirm.
  const [redeliverOpen, setRedeliverOpen] = useState(false);
  const m = detail.data;

  /**
   * Admin action on this message, confirmed through the shared dialog host.
   * The confirm stacks over the route modal; a rejected mutation keeps the
   * dialog open with the server's message (useMessageAction suppresses the
   * global error toast). On success the modal closes back to the list.
   */
  function runAction(action: ConfirmedAction, message: MessageDetail) {
    const copy = ACTION_COPY[action];
    void confirm({
      title: copy.title,
      description: copy.body(message.to_address),
      confirmLabel: copy.confirmLabel,
      variant: copy.danger ? "danger" : undefined,
      onConfirm: async () => {
        await messageAction.mutateAsync({ id: message.id, action });
      },
    }).then((confirmed) => {
      if (confirmed) {
        notifySuccess(copy.success);
        close();
      }
    });
  }

  const hasHtml = !!m?.body_html;
  const hasText = !!m?.body_text;
  // A body tab that turns out to be empty falls back rather than showing a
  // blank panel (a message can carry text with no HTML part, never neither).
  const activeTab: DetailTab =
    tab === "html" && !hasHtml ? (hasText ? "text" : "timeline") : tab === "text" && !hasText ? "timeline" : tab;

  return (
    <RouteModal
      title={
        m ? (
          <span className={styles.modalTitle}>
            Message details <MessageStateBadge state={m.state} />
          </span>
        ) : (
          "Message details"
        )
      }
      // The heading carries a badge, so the dialog's accessible name is given
      // as text rather than read off the node (adminer 0.44).
      aria-label="Message details"
      // The address panel runs edge to edge; each section below sets its own
      // padding instead of the body's.
      bodyPadding="none"
      fallback={{ pathname: "/messages", search: location.search }}
      width={860}
      footer={
        <>
          {isAdmin && m && RETRYABLE_STATES.includes(m.state) && (
            <Button variant="primary" weight="outline" onClick={() => runAction("retry", m)}>
              Retry
            </Button>
          )}
          {/* Cancel is one of several footer actions, not the dialog's primary
              — keep the quiet outline face rather than 0.33's solid danger. */}
          {isAdmin && m && CANCELLABLE_STATES.includes(m.state) && (
            <Button variant="danger" weight="outline" onClick={() => runAction("cancel", m)}>
              Cancel
            </Button>
          )}
          {isAdmin && m && REDELIVERABLE_STATES.includes(m.state) && (
            <Button variant="primary" weight="outline" onClick={() => setRedeliverOpen(true)}>
              Redeliver
            </Button>
          )}
          {isAdmin &&
            m &&
            REDELIVERABLE_STATES.includes(m.state) &&
            !!m.template_key && (
              <Button variant="primary" weight="outline" onClick={() => runAction("rerender", m)}>
                Re-render and deliver
              </Button>
            )}
          <Button onClick={close}>Close</Button>
        </>
      }
    >
      {detail.isLoading ? (
        <LoadingState label="Loading message…" />
      ) : detail.error ? (
        <ErrorState
          error={detail.error}
          title="Failed to load the message."
          onRetry={detail.refetch}
        />
      ) : m ? (
        <div className={styles.detailStack}>
          {/* The envelope: who it is addressed to, and the mark the message
              came back with. The barcode is drawn from the message ID printed
              under it — the same fingerprint every time, no two alike. */}
          <div className={styles.envelope}>
            <div className={styles.address}>
              <span className={styles.addressKey}>To</span>
              <span className={styles.addressTo}>
                {m.to_name ? `${m.to_name} <${m.to_address}>` : m.to_address}
              </span>
              <span className={styles.addressKey}>
                {m.channel === "sms" ? "Message" : "Subject"}
              </span>
              <span className={styles.addressSubject}>
                {m.channel === "sms" ? (
                  // An SMS has no subject, so this row carried a badge saying
                  // "SMS" on a page that says so twice already. What is worth
                  // knowing after the fact is what the message cost: the
                  // count is of the stored, rendered body, so unlike the
                  // editor's estimate this is what was actually billed.
                  m.body_text ? (
                    <Text variant="muted">{smsHint(m.body_text)}</Text>
                  ) : (
                    <Text variant="muted">— no text —</Text>
                  )
                ) : (
                  m.subject || <Text variant="muted">— no subject —</Text>
                )}
                {m.is_test && (
                  <>
                    {" "}
                    <Badge variant="muted">Test</Badge>
                  </>
                )}
              </span>
              <MailBarcode value={m.id} className={styles.addressBarcode} />
              <span className={styles.addressId}>{m.id}</span>
            </div>
            <Postmark
              state={m.state}
              senderKey={m.sender_key}
              at={m.sent_at ?? m.created_at}
              className={styles.postmark}
            />
          </div>
          <DescriptionList labelWidth={130} className={styles.detailFields}>
            {m.preheader && (
              <DescriptionList.Item label="Preheader">
                {m.preheader}
              </DescriptionList.Item>
            )}
            <DescriptionList.Item label="Category">
              {labelCategory(m.category)}
            </DescriptionList.Item>
            <DescriptionList.Item label="Sender">
              <SenderChip senderKey={m.sender_key} />
            </DescriptionList.Item>
            {m.template_key && (
              <DescriptionList.Item label="Template">
                <Text variant="mono">{m.template_key}</Text>
              </DescriptionList.Item>
            )}
            {m.batch_id && (
              <DescriptionList.Item label="Batch">
                <Text variant="mono">{m.batch_id}</Text>
              </DescriptionList.Item>
            )}
            {m.reference && (
              <DescriptionList.Item label="Reference">
                <Text variant="mono">{m.reference}</Text>
              </DescriptionList.Item>
            )}
            {m.expires_at && (
              <DescriptionList.Item label="Deadline">
                <Text variant="mono">{formatDateTime(m.expires_at)}</Text>
              </DescriptionList.Item>
            )}
            {m.metadata && Object.keys(m.metadata).length > 0 && (
              <DescriptionList.Item label="Metadata">
                {Object.entries(m.metadata).map(([key, value]) => (
                  <div key={key}>
                    <Text variant="mono">{key}</Text>: {value}
                  </div>
                ))}
              </DescriptionList.Item>
            )}
            <DescriptionList.Item label="Attempts">
              {m.attempts}
              {m.max_attempts ? ` / ${m.max_attempts}` : ""}
            </DescriptionList.Item>
            {m.next_attempt_at && m.state === "pending" && (
              <DescriptionList.Item label="Next attempt">
                <Text variant="mono">{formatDateTime(m.next_attempt_at)}</Text>
              </DescriptionList.Item>
            )}
            {m.sent_at && (
              <DescriptionList.Item label="Sent at">
                <Text variant="mono">{formatDateTime(m.sent_at)}</Text>
              </DescriptionList.Item>
            )}
            {m.provider_message_id && (
              <DescriptionList.Item label="Provider message ID">
                <Text variant="mono">{m.provider_message_id}</Text>
              </DescriptionList.Item>
            )}
            {m.suppressed_reason && (
              <DescriptionList.Item label="Suppression reason">
                {m.suppressed_reason}
              </DescriptionList.Item>
            )}
            {m.redelivered_from && (
              <DescriptionList.Item label="Redelivered from">
                <Text variant="mono">{m.redelivered_from}</Text>
              </DescriptionList.Item>
            )}
            {m.attachments && m.attachments.length > 0 && (
              <DescriptionList.Item label="Attachments">
                {m.attachments.map((a) => (
                  <div key={a.index}>
                    {/* Only a stored file can be downloaded: a suppressed
                        message keeps the name alone, and retention drops the
                        content of old messages. */}
                    {a.stored ? (
                      <a
                        href={`/api/messages/${encodeURIComponent(m.id)}/attachments/${a.index}`}
                        download={a.filename}
                      >
                        {a.filename}
                      </a>
                    ) : (
                      <span>{a.filename}</span>
                    )}{" "}
                    <Text variant="muted">
                      {a.content_type}, {formatSize(a.size)}
                      {!a.stored &&
                        (m.state === "suppressed"
                          ? ", not stored — message suppressed"
                          : ", removed after retention")}
                    </Text>
                  </div>
                ))}
              </DescriptionList.Item>
            )}
            <DescriptionList.Item label="Created at">
              <Text variant="mono">{formatDateTime(m.created_at)}</Text>
            </DescriptionList.Item>
          </DescriptionList>

          {m.last_error && (
            <Alert variant="err" title={`Last error${m.last_error_class ? ` (${m.last_error_class})` : ""}`}>
              <span className={styles.errorText}>{m.last_error}</span>
            </Alert>
          )}

          <div className={styles.tabsBlock}>
            <Tabs
              aria-label="Message detail"
              tabs={[
                { id: "timeline", label: "Delivery timeline" },
                { id: "html", label: "HTML", disabled: !hasHtml },
                { id: "text", label: "Text", disabled: !hasText },
              ]}
              value={activeTab}
              onChange={(v) => setTab(v as DetailTab)}
            />
            {activeTab === "timeline" ? (
              <DeliveryTimeline
                messageId={m.id}
                channel={m.channel}
                live={isLiveState(m.state)}
              />
            ) : activeTab === "html" && hasHtml ? (
              <iframe
                className={styles.bodyFrame}
                sandbox=""
                srcDoc={m.body_html}
                title="HTML body preview"
              />
            ) : (
              <pre className={styles.bodyText}>{m.body_text}</pre>
            )}
          </div>

          <JsonBlock json={m.template_data_json} label="Template data" />
          <JsonBlock json={m.payload_json} label="Payload" />
        </div>
      ) : null}
      {redeliverOpen && m && (
        <RedeliverModal
          message={m}
          onClose={() => setRedeliverOpen(false)}
          onDone={() => {
            setRedeliverOpen(false);
            close();
          }}
        />
      )}
    </RouteModal>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
